import { cn } from "@omni/utils/styles";
import type { SliderProps } from "rc-slider";
import Slider from "rc-slider";
import type { TooltipRef } from "rc-tooltip";
import Tooltip from "rc-tooltip";
import raf from "rc-util/lib/raf";
import { FC, ReactElement, ReactNode, useEffect, useRef } from "react";

type HandleTooltipProps = {
    value: number;
    children: ReactElement;
    visible: boolean;
    tipFormatter?: (value: number) => ReactNode;
};

const HandleTooltip = (props: HandleTooltipProps) => {
    const {
        value,
        children,
        visible,
        tipFormatter = (val) => `${val}`,
        ...restProps
    } = props;

    const tooltipRef = useRef<TooltipRef>(null);
    const rafRef = useRef<number | null>(null);

    function cancelKeepAlign() {
        raf.cancel(rafRef.current!);
    }

    function keepAlign() {
        rafRef.current = raf(() => {
            tooltipRef.current?.forceAlign();
        });
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (visible) {
            keepAlign();
        } else {
            cancelKeepAlign();
        }

        return cancelKeepAlign;
    }, [value, visible]);

    return (
        <Tooltip
            placement="top"
            overlay={tipFormatter(value)}
            overlayInnerStyle={{
                minHeight: "auto",
                padding: ".2rem",
                marginBottom: "0"
            }}
            classNames={{
                root: cn("tw:p-1.25!")
            }}
            ref={tooltipRef}
            visible={visible}
            {...restProps}
        >
            {children}
        </Tooltip>
    );
};

const createHandleRender: (
    tipFormatter?: (value: number) => ReactNode,
    tipProps?: any
) => SliderProps["handleRender"] =
    (tipFormatter, tipProps) => (node, handleProps) => (
        <HandleTooltip
            value={handleProps.value}
            visible={handleProps.dragging}
            tipFormatter={tipFormatter}
            {...tipProps}
        >
            {node}
        </HandleTooltip>
    );

interface TooltipSliderProps extends SliderProps {
    tipVisible?: boolean;
    tipFormatter?: (value: number) => ReactNode;
    tipProps?: any;
}

const TooltipSlider: FC<TooltipSliderProps> = ({
    tipVisible,
    tipFormatter,
    tipProps,
    ...props
}) => {
    const handleRenderer = tipVisible
        ? createHandleRender(tipFormatter, tipProps)
        : undefined;

    return (
        <Slider
            {...props}
            classNames={{
                track: cn("tw:bg-primary!"),
                handle: cn("tw:border-primary! tw:opacity-100!")
            }}
            handleRender={handleRenderer}
        />
    );
};

export default TooltipSlider;
